
import React, { useState } from 'react';
import SaveSubjectModal from "./SaveSubjectModal";
import ConfirmDialog from "popup/ConfirmDialog";
import { Download, Edit, Plus, Trash } from 'lucide-react';


interface SubjectPlanningPageProps {
    data: {
        licenseClassId?: string;
        licenseClassName?: string;
        courseId?: string;
        courseName?: string;
        practices?: any[];
        theories?: any[];
    };
    onSubjectSubmit: (subjectData: any) => Promise<void>;
    onDeleteSubject: (id: string) => Promise<void>;
    onRefresh?: () => void;
    onExport?: () => void;
}

export default function SubjectPlanningPage({
                                                data,
                                                onSubjectSubmit,
                                                onDeleteSubject,
                                                onRefresh,
                                                onExport
                                            }: SubjectPlanningPageProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentSubject, setCurrentSubject] = useState<any>(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [subjectToDelete, setSubjectToDelete] = useState<string | null>(null);
    const existingSubjects = [
        ...(data.theories || []).map(t => t.subjectName),
        ...(data.practices || []).map(p => p.subjectName)
    ];

    const handleAddSubject = () => {
        setCurrentSubject(null);
        setIsModalOpen(true);
    };

    const handleEditSubject = (subject: any, type: 'theory' | 'practice') => {
        setCurrentSubject({
            ...subject,
            subjectType: type === 'theory' ? 1 : 2,
        });
        setIsModalOpen(true);
    };

    const handleDeleteClick = (subject: any) => {
        setSubjectToDelete(subject.id);
        setIsDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (subjectToDelete) {
            await onDeleteSubject(subjectToDelete);
        }
        setIsDeleteConfirmOpen(false);
        setSubjectToDelete(null);
    };

    const calculateTotalHours = (items: any[]) => {
        return items.reduce((total, item) => {
            return (
                total +
                (item.theoryHours || 0) +
                (item.practiceHours || 0) +
                (item.openRoadPracticeHours || 0) +
                (item.roadPracticeHours || 0) +
                (item.testHours || 0)
            );
        }, 0);
    };
    const renderHourValue = (value: number | null | undefined) => {
        return value ? value : '';
    }

    const totalTheoryHours = calculateTotalHours(data.theories || []);
    const totalPracticeHours = calculateTotalHours(data.practices || []);
    const totalHours = totalTheoryHours + totalPracticeHours;

    const title = data.courseName
        ? `Kế hoạch đào tạo khóa: ${data.courseName}`
        : `Kế hoạch đào tạo hạng: ${data.licenseClassName || 'Không có tên'}`;

    return (
        <div className="mt-4">
            <div className="md:flex md:justify-between md:items-center mb-4">
                <h1 className="text-2xl font-bold mb-3">{title}</h1>
                <div className={'md:flex'}>
                    <button
                        onClick={handleAddSubject}
                        className="flex items-center w-full md:w-auto px-4 py-2 mr-2 mb-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <Plus /> Thêm môn học
                    </button>
                    <button
                        onClick={onExport}
                        className="flex items-center w-full md:w-auto px-4 py-2 mb-3 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                        <Download className="mr-1"/> Xuất kế hoạch đào tạo
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border border-gray-300 mb-6">
                    <thead>

                    <tr className="bg-gray-100 text-center">
                        <th className="border border-gray-300 p-2" rowSpan={2}>STT</th>
                        <th className="border border-gray-300 p-2" rowSpan={2}>Môn học</th>
                        <th className="border border-gray-300 p-2" rowSpan={2}>Tổng số (giờ)</th>
                        <th className="border border-gray-300 p-2" colSpan={2}>Lý thuyết (giờ)</th>
                        <th className="border border-gray-300 p-2" rowSpan={2}>Thực hành trong hình (giờ)</th>
                        <th className="border border-gray-300 p-2" rowSpan={2}>Thực hành trên đường (giờ)</th>
                        <th className="border border-gray-300 p-2" rowSpan={2}>Kiểm tra hết môn (giờ)</th>
                        <th className="border border-gray-300 p-2" rowSpan={2}>Ghi chú</th>
                        <th className="border border-gray-300 p-2" rowSpan={2}>Thao tác</th>
                    </tr>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 p-2">LT</th>
                        <th className="border border-gray-300 p-2">TH</th>
                    </tr>
                    </thead>
                    <tbody>
                    {/* Theory Section */}
                    <tr>
                        <th colSpan={10} className="border border-gray-300 p-2 text-start text-lg">
                            Lý thuyết
                        </th>
                    </tr>
                    {(data.theories || []).map((subject: any, index: number) => {
                        const total = calculateTotalHours([subject]);
                        return (
                            <tr key={index}>
                                <td className="border border-gray-300 p-2 text-center">{index + 1}</td>
                                <td className="border border-gray-300 p-2">{subject.subjectName}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(total)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.theoryHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.practiceHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.openRoadPracticeHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.roadPracticeHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.testHours)}</td>
                                <td className="border border-gray-300 p-2">{subject.notes || ""}</td>
                                <td className="border border-gray-300 p-2 text-center">
                                    <div className="md:flex md:justify-center space-x-2">
                                        <button
                                            onClick={() => handleEditSubject(subject, 'theory')}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            <Edit className="inline" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(subject)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <Trash className="inline" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}

                    <tr>
                        <th colSpan={10} className="border border-gray-300 p-2 text-start text-lg">
                            Thực hành
                        </th>
                    </tr>

                    {/* Practice Section */}
                    {(data.practices || []).map((subject: any, index: number) => {
                        const total = calculateTotalHours([subject]);
                        return (
                            <tr key={index}>
                                <td className="border border-gray-300 p-2 text-center">{index + 1}</td>
                                <td className="border border-gray-300 p-2">{subject.subjectName}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(total)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.theoryHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.practiceHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.openRoadPracticeHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.roadPracticeHours)}</td>
                                <td className="border border-gray-300 p-2 text-center">{renderHourValue(subject.testHours)}</td>
                                <td className="border border-gray-300 p-2">{subject.notes || ""}</td>
                                <td className="border border-gray-300 p-2 text-center">
                                    <div className="flex justify-center space-x-2">
                                        <button
                                            onClick={() => handleEditSubject(subject, 'practice')}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            <Edit className="inline" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(subject)}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <Trash className="inline" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800">Tổng hợp số giờ đào tạo</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
                    {/* Lý thuyết */}
                    <div className="p-6 text-center">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-medium text-gray-500 mb-1">Tổng giờ lý thuyết</span>
                            <span className="text-2xl font-bold text-blue-600">
                                    {totalTheoryHours.toLocaleString()} giờ
                                </span>
                            <div className="mt-2 w-16 h-1 bg-blue-100 rounded-full"></div>
                        </div>
                    </div>

                    {/* Thực hành */}
                    <div className="p-6 text-center">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-medium text-gray-500 mb-1">Tổng giờ thực hành</span>
                            <span className="text-2xl font-bold text-green-600">
                                    {totalPracticeHours.toLocaleString()} giờ
                                </span>
                            <div className="mt-2 w-16 h-1 bg-green-100 rounded-full"></div>
                        </div>
                    </div>

                    {/* Tổng cộng */}
                    <div className="p-6 text-center">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-medium text-gray-500 mb-1">Tổng số giờ đào tạo</span>
                            <span className="text-2xl font-bold text-purple-600">
                                    {totalHours.toLocaleString()} giờ
                                </span>
                            <div className="mt-2 w-16 h-1 bg-purple-100 rounded-full"></div>
                        </div>
                    </div>
                </div>
            </div>

            <SaveSubjectModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={onSubjectSubmit}
                initialData={currentSubject}
                licenseClassId={data.licenseClassId}
                courseId={data.courseId}
                existingSubjects={existingSubjects}
            />

            <ConfirmDialog
                isOpen={isDeleteConfirmOpen}
                title="Xác nhận xóa môn học"
                description="Bạn có chắc chắn muốn xóa môn học này?"
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
}